import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ModalWrapper } from '../../components/modalWrapper'
import { stringifyQuery } from '../../helpers/queryHelper'
import './CallToolsDealsModal.css'

// Mirrors Crm::Inbound::SyncDecision::EDITABLE_STATUSES on the API. Only these
// three carry no money, signature or remittance, so only these may be continued
// as an application.
export const EDITABLE_STATUSES = ['empty', 'quoted', 'selected']

// Statuses that do not offer Continue as Customer.
//
// `quoted` — the deal is alive, but the agent has only priced it, so the
// handoff is premature: the agent finishes the quote on Continue Application
// rather than dropping the customer into a flow with nothing selected yet.
// `selected` deliberately keeps the button.
//
// `agreement_signed` — the paperwork is done but the deal has not remitted, so
// it is still mid-flight rather than finished.
//
// The finished deals — `remitted`, `cancelled`, `back_out` — deliberately keep
// the button. PackagesController::THANK_YOU_STATUSES lands all three on the
// thank-you page, which is the summary support walks the customer through.
export const NO_CUSTOMER_FLOW_STATUSES = ['agreement_signed', 'quoted']

export const hasCustomerFlow = (status?: string | null) =>
  !NO_CUSTOMER_FLOW_STATUSES.includes(status || '')

export const isEditable = (status?: string | null) =>
  EDITABLE_STATUSES.includes(status || '')

// A deal whose first payment is already scheduled carries a payment commitment,
// so it is locked whatever its status says — re-entering the wizard would
// rewrite terms the customer agreed to. Mirrors Crm::Inbound::SyncDecision,
// which drops the same deal to continue_as_customer on the next sync.
export const hasScheduledFirstPayment = (productPackage: DealPackage) =>
  Boolean(productPackage.firstPaymentDate)

// Deals per page inside the modal. The two requests behind it load up to 25
// rows each, so paging is over what is already in memory.
export const DEALS_PER_PAGE = 10

export const vehicleLabelFor = (vehicle?: DealVehicle | null) => {
  const label = [vehicle?.year, vehicle?.make, vehicle?.model]
    .filter(Boolean)
    .join(' ')

  return label || 'Vehicle unknown'
}

const humanizeStatus = (status?: string | null) =>
  (status || 'unknown')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : '—'

export interface DealVehicle {
  id?: string | null
  year?: string | number | null
  make?: string | null
  model?: string | null
  vin?: string | null
  plateNumber?: string | null
  plateState?: string | null
  mileage?: string | number | null
  condition?: string | null
  transmission?: string | null
  selectedVehicleTrimId?: string | number | null
}

export interface DealPackage {
  id: string
  status?: string | null
  customerToken?: string | null
  createdAt?: string | null
  remittedAt?: string | null
  firstPaymentDate?: string | null
  admin?: { name?: string | null; email?: string | null } | null
  // The deal's own customer, not the profile the modal opened on: deals are
  // matched org-wide by vehicle, so a row can belong to someone else.
  user?: { id?: string | null; name?: string | null } | null
  vehicle?: DealVehicle | null
}

interface Props {
  isOpen: boolean
  onClose: () => void
  packages: DealPackage[]
  /** the vehicle CallTools just synced, so its rows can be called out */
  syncedVehicleId?: string | null
  onContinueApplication: (productPackage: DealPackage) => void
  onStartNewDeal: () => void
}

const continueAsCustomerUrl = (productPackage: DealPackage) => {
  const base = `${process.env.REACT_APP_BLINKER_API_URL}/packages/${productPackage.customerToken}`
  const email = productPackage.admin?.email

  return email ? `${base}?agent_email=${encodeURIComponent(email)}` : base
}

// Query string for the New Deal hand-off to /refiapp.
//
// Carries the vehicle's VALUES and deliberately omits its id. With a vehicleId
// the wizard would point the application at this vehicle row, and the Vehicle
// step posts `id: vehicle.id` (vehicleDetailSection#onPlateVinSubmit), so any
// edit the agent made would rewrite the car the other deals on this list sit
// on. Without it the submit creates a vehicle instead.
//
// Keys match the vehicle entity's own field names so plateVinForm reads the
// prefill through the exact mapping it already uses for a saved vehicle.
export const newDealSearch = (userId: string, vehicle?: DealVehicle | null) =>
  stringifyQuery({
    userId,
    // Tells the wizard to start a deal rather than resume one, so
    // ProductPackages::Create does not hand back the quoted/selected package
    // the agent just declined in this modal.
    fresh: 1,
    year: vehicle?.year,
    make: vehicle?.make,
    model: vehicle?.model,
    vin: vehicle?.vin,
    plateNumber: vehicle?.plateNumber,
    plateState: vehicle?.plateState,
    mileage: vehicle?.mileage,
    condition: vehicle?.condition,
    transmission: vehicle?.transmission,
    selectedVehicleTrimId: vehicle?.selectedVehicleTrimId,
  })

const CallToolsDealsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  packages,
  syncedVehicleId,
  onContinueApplication,
  onStartNewDeal,
}) => {
  const [page, setPage] = useState(0)

  // Reopening the modal, or loading a different set of deals, starts at page 1
  // rather than wherever the agent left off last time.
  useEffect(() => {
    setPage(0)
  }, [isOpen, packages.length])

  // Hooks run before this: bailing earlier would make them conditional.
  if (!isOpen) return null

  const syncedDeal = packages.find(
    (productPackage) =>
      String(productPackage.vehicle?.id) === String(syncedVehicleId)
  )
  const vehicleLabel = syncedDeal
    ? vehicleLabelFor(syncedDeal.vehicle)
    : 'this vehicle'

  const pageCount = Math.max(1, Math.ceil(packages.length / DEALS_PER_PAGE))
  const currentPage = Math.min(page, pageCount - 1)
  const firstOnPage = currentPage * DEALS_PER_PAGE
  const visiblePackages = packages.slice(
    firstOnPage,
    firstOnPage + DEALS_PER_PAGE
  )

  return (
    <ModalWrapper
      title="Select a deal"
      hideModal={onClose}
      xclose={true}
      width="720px"
      height="auto"
    >
      <div className="calltools-deals">
        <p className="calltools-deals__description">
          CallTools synced <strong>{vehicleLabel}</strong>.{' '}
          {packages.length === 1
            ? 'There is 1 deal'
            : `There are ${packages.length} deals`}{' '}
          on this vehicle or already open for this customer. Pick the one to
          work on, or start a new deal.
        </p>

        <ul className="calltools-deals__list">
          {visiblePackages.map((productPackage) => {
            const firstPaymentScheduled =
              hasScheduledFirstPayment(productPackage)
            const editable =
              isEditable(productPackage.status) && !firstPaymentScheduled
            const dealUserId = productPackage.user?.id

            return (
              <li className="calltools-deals__item" key={productPackage.id}>
                <div className="calltools-deals__meta">
                  <span className="calltools-deals__status">
                    {humanizeStatus(productPackage.status)}
                  </span>
                  <span className="calltools-deals__detail">
                    Deal #{productPackage.id}
                  </span>
                  {/* The list mixes vehicles — other customers' deals on the
                      synced car, plus this customer's deals on their other
                      cars — so every row has to name its own. */}
                  <span className="calltools-deals__detail">
                    {vehicleLabelFor(productPackage.vehicle)}
                    {productPackage.vehicle?.vin
                      ? ` · ${productPackage.vehicle.vin}`
                      : ''}
                    {String(productPackage.vehicle?.id) ===
                    String(syncedVehicleId)
                      ? ' · just synced'
                      : ''}
                  </span>
                  {/* Deals are matched org-wide by vehicle, so this row may
                      belong to a different customer than the profile behind the
                      modal. Link straight to whoever owns it. */}
                  {dealUserId && (
                    <Link
                      className="calltools-deals__customer"
                      to={`/users/${dealUserId}`}
                    >
                      {productPackage.user?.name || `Customer #${dealUserId}`}
                    </Link>
                  )}
                  <span className="calltools-deals__detail">
                    {productPackage.admin?.name ||
                      productPackage.admin?.email ||
                      'Unassigned'}
                  </span>
                  <span className="calltools-deals__detail">
                    Created {formatDate(productPackage.createdAt)}
                  </span>
                  {firstPaymentScheduled && (
                    <span className="calltools-deals__detail">
                      First payment{' '}
                      {formatDate(productPackage.firstPaymentDate)}
                    </span>
                  )}
                </div>

                <div className="calltools-deals__actions">
                  {editable && (
                    <button
                      type="button"
                      className="button button-drive-america"
                      onClick={() => onContinueApplication(productPackage)}
                    >
                      Continue Application
                    </button>
                  )}

                  {/* Locked by money, a signature, or a dead EFS contract.
                      Saying why beats a disabled button with no explanation.

                      A scheduled first payment is deliberately silent: the row
                      already prints the date, so the label only repeated it.
                      Dropping Continue Application is the whole signal. */}
                  {!editable && !firstPaymentScheduled && (
                    <span className="calltools-deals__locked">
                      Locked — {humanizeStatus(productPackage.status)}
                    </span>
                  )}

                  {productPackage.customerToken &&
                    hasCustomerFlow(productPackage.status) && (
                      <a
                        className="button button-drive-america"
                        href={continueAsCustomerUrl(productPackage)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Continue as Customer
                      </a>
                    )}
                </div>
              </li>
            )
          })}
        </ul>

        {pageCount > 1 && (
          <div className="calltools-deals__pagination">
            <button
              type="button"
              className="button button-drive-america"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              Previous
            </button>
            <span className="calltools-deals__detail">
              Showing {firstOnPage + 1}–{firstOnPage + visiblePackages.length}{' '}
              of {packages.length}
            </span>
            <button
              type="button"
              className="button button-drive-america"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}

        {/* None of the deals above is the one the agent is calling about: start
            a fresh one on the same vehicle rather than reworking a locked deal. */}
        <div className="calltools-deals__footer">
          <button
            type="button"
            className="button button-drive-america"
            onClick={onStartNewDeal}
          >
            New Deal
          </button>
        </div>
      </div>
    </ModalWrapper>
  )
}

export default CallToolsDealsModal
